// Microsoft Graph mail client for the Krapf charter inbox.
//
// Delegated access in the browser (MSAL, auth-code + PKCE): the operator signs in with their own
// Microsoft account and the page reads and writes THEIR mailbox. No client secret, no server-side
// token storage, no mailbox-wide application permission — the app can only ever see what the signed-in
// operator can see.
//
// Scopes: Mail.ReadWrite (read the inbox AND create the reply draft) + Mail.Send + User.Read. These
// are normally user-consentable, but a work/school tenant that has disabled user consent will need an
// administrator to approve them once.
//
// Replies are created as real Outlook DRAFTS (createReply -> patch body -> send). Three reasons:
// threading headers (In-Reply-To/References) are set by Graph rather than by us; the draft is visible
// in the operator's own Outlook, so nothing happens invisibly; and it matches the product decision
// that the agent drafts and a human sends.
//
// Pure-ish module: no React. `configure()` is called once at page mount.
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

const GRAPH = 'https://graph.microsoft.com/v1.0';
export const SCOPES = ['Mail.ReadWrite', 'Mail.Send', 'User.Read'];

let msal = null;
let ready = null;

/**
 * `clientId` is the Azure app registration (public SPA client — not a secret; it is safe in the
 * bundle and is useless without an interactive user sign-in). `authority` defaults to /common so both
 * work accounts and personal Outlook.com accounts can sign in.
 */
export function configure({ clientId, authority } = {}) {
  const id = String(clientId || '').trim();
  if (!id) return false;
  if (msal) return true;
  msal = new PublicClientApplication({
    auth: {
      clientId: id,
      authority: authority || 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin + window.location.pathname,
    },
    // sessionStorage: the sign-in lasts the tab, not forever, which is the right default for a tool
    // pointed at someone's real mailbox on a shared demo machine.
    cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
  });
  ready = msal.initialize();
  return true;
}

export function isConfigured() {
  return !!msal;
}

async function client() {
  if (!msal) throw new Error('Microsoft sign-in is not configured for this environment.');
  await ready;
  return msal;
}

export async function currentAccount() {
  if (!msal) return null;
  await ready;
  const accounts = msal.getAllAccounts();
  return accounts.length ? accounts[0] : null;
}

export async function signIn() {
  const app = await client();
  const res = await app.loginPopup({ scopes: SCOPES, prompt: 'select_account' });
  return res.account;
}

export async function signOut() {
  const app = await client();
  const account = await currentAccount();
  await app.logoutPopup({ account });
}

async function token() {
  const app = await client();
  const account = await currentAccount();
  if (!account) throw new Error('Not signed in to Microsoft.');
  try {
    const res = await app.acquireTokenSilent({ scopes: SCOPES, account });
    return res.accessToken;
  } catch (e) {
    // A silent refresh fails on consent changes, MFA, or an expired refresh token. Falling back to a
    // popup is the documented path; anything else would strand the operator with a dead inbox.
    if (e instanceof InteractionRequiredAuthError) {
      const res = await app.acquireTokenPopup({ scopes: SCOPES, account });
      return res.accessToken;
    }
    throw e;
  }
}

async function graph(path, { method = 'GET', body } = {}) {
  const accessToken = await token();
  const res = await fetch(`${GRAPH}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    let detail = `Microsoft Graph ${method} ${path} failed (${res.status})`;
    try {
      const b = await res.json();
      if (b && b.error && b.error.message) detail = b.error.message;
    } catch (e) { /* non-JSON error body */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const SELECT = [
  'id', 'conversationId', 'subject', 'from', 'toRecipients', 'receivedDateTime',
  'bodyPreview', 'body', 'isDraft', 'internetMessageHeaders',
].join(',');

/** Normalize a Graph message into the shape emailText/krapfIntake expect. */
function toMessage(m) {
  const addr = (m.from && m.from.emailAddress) || {};
  const headers = {};
  (m.internetMessageHeaders || []).forEach((h) => { headers[h.name] = h.value; });
  return {
    id: m.id,
    conversationId: m.conversationId,
    subject: m.subject || '',
    fromName: addr.name || '',
    fromAddress: addr.address || '',
    receivedAt: m.receivedDateTime,
    preview: m.bodyPreview || '',
    body: (m.body && m.body.content) || '',
    bodyIsHtml: !!(m.body && m.body.contentType === 'html'),
    isDraft: !!m.isDraft,
    headers,
  };
}

/**
 * Recent inbox messages grouped into threads, newest first.
 *
 * A real mailbox is mostly noise, which is exactly why an operator has to mark a thread as a trip
 * inquiry by hand — nothing here tries to guess which mail is charter business.
 */
export async function listThreads({ top = 40 } = {}) {
  const data = await graph(
    `/me/mailFolders/inbox/messages?$top=${top}&$select=${SELECT}&$orderby=receivedDateTime desc`,
  );
  const byConversation = new Map();
  (data.value || []).map(toMessage).forEach((msg) => {
    if (msg.isDraft) return;
    const existing = byConversation.get(msg.conversationId);
    if (existing) {
      existing.messages.push(msg);
      if (msg.receivedAt > existing.latest.receivedAt) existing.latest = msg;
    } else {
      byConversation.set(msg.conversationId, {
        conversationId: msg.conversationId,
        subject: msg.subject,
        latest: msg,
        messages: [msg],
      });
    }
  });
  return [...byConversation.values()]
    .map((t) => ({ ...t, messages: t.messages.slice().sort((a, b) => a.receivedAt.localeCompare(b.receivedAt)) }))
    .sort((a, b) => b.latest.receivedAt.localeCompare(a.latest.receivedAt));
}

/** Every message in one thread, oldest first — including our own sent replies. */
export async function getThread(conversationId) {
  const data = await graph(
    `/me/messages?$filter=conversationId eq '${conversationId}'&$select=${SELECT}&$top=50`,
  );
  return (data.value || [])
    .map(toMessage)
    .filter((m) => !m.isDraft)
    .sort((a, b) => String(a.receivedAt).localeCompare(String(b.receivedAt)));
}

/**
 * Create a reply draft on a message with our composed body. Returns the draft id.
 * createReply gives us correct threading headers and the quoted original; we then replace the body
 * with our text, keeping Graph's quoted history beneath it.
 */
export async function createReplyDraft(messageId, bodyText) {
  const draft = await graph(`/me/messages/${messageId}/createReply`, { method: 'POST' });
  const quoted = (draft.body && draft.body.content) || '';
  // Our text goes ABOVE the quoted original — the top-post convention every mail client expects, and
  // the one emailText.stripQuotedAndSignature assumes when the customer replies back.
  const html = `<div>${escapeHtml(bodyText).replace(/\n/g, '<br>')}</div>${quoted}`;
  await graph(`/me/messages/${draft.id}`, {
    method: 'PATCH',
    body: { body: { contentType: 'html', content: html } },
  });
  return draft.id;
}

export async function sendDraft(draftId) {
  await graph(`/me/messages/${draftId}/send`, { method: 'POST' });
}

export async function discardDraft(draftId) {
  await graph(`/me/messages/${draftId}`, { method: 'DELETE' });
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
